import { debounce } from '@mui/material'
import { CargoType, CarrierVisitStatus, RailVisitResponseDto } from '@planning/app/api'
import railVisitService from '@planning/services/railVisitService'
import { formatDateTime } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import { TomAutoComplete, TomAutoCompleteProps, TrainIcon } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'

type Props = {
  selectedRailVisit?: RailVisitResponseDto
  handleOnChange?: (value: RailVisitResponseDto | undefined) => void
  cargoType?: CargoType
  statuses?: CarrierVisitStatus[]
} & TomAutoCompleteProps<RailVisitResponseDto>

export const RailVisitAutoComplete: FC<Props> = observer(props => {
  const { selectedRailVisit, label, cargoType, sx, dataCy, handleOnChange, statuses } = props
  const { t } = useTranslate()

  const [options, setOptions] = useState<RailVisitResponseDto[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const onSearchFilterChange = debounce(async (filter?: string) => {
    try {
      setLoading(true)
      setOptions((await railVisitService.get(1, 10, statuses, filter, cargoType)).payload)
    } finally {
      setLoading(false)
    }
  }, 500)

  const getArrivalDate = (visit: RailVisitResponseDto) => {
    if (visit.ata) return `${t('ata', 'ATA')}: ${formatDateTime(visit.ata)}`
    else if (visit.eta) return `${t('eta', 'ETA')}: ${formatDateTime(visit.eta)}`

    return ''
  }

  return (
    <TomAutoComplete
      {...props}
      sx={sx ?? { mb: '1rem' }}
      label={label ?? t('railVisit', 'Rail visit')}
      entityKey='id'
      getOptionLabel={railVisit => `${railVisit.identifier} - ${getArrivalDate(railVisit)}`}
      options={options}
      defaultValue={selectedRailVisit}
      loading={loading}
      onChangeEntity={entity => {
        if (!entity) {
          setOptions([])
          return
        }

        handleOnChange?.(entity)
      }}
      onChangeFilter={filter => {
        if (!filter) {
          setOptions([])
          return
        }

        onSearchFilterChange?.(filter)
      }}
      startAdornment={<TrainIcon />}
      dataCy={dataCy ?? 'rail-visit-transport-autocomplete'}
    />
  )
})
